import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Check, Star, Crown, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function UpgradeModal({ isOpen, onClose, onUpgrade }) {
  const navigate = useNavigate();

  const handleUpgrade = (planType) => {
    // Redirect to payment checkout page
    navigate(`/payment/checkout?plan=${planType}`);
    onClose();
  };

  const vipFeatures = [
    {
      title: '1K Generations/month',
      description: 'Generate up to 1,000 indicators per month'
    },
    {
      title: 'Advanced AI Powered',
      description: 'Access to advanced AI for better code quality'
    },
    {
      title: 'Intermediate Code Pine Script SL & TP',
      description: 'Stop Loss & Take Profit integration'
    },
    {
      title: 'Generation History',
      description: 'Access all your previously generated code'
    },
    {
      title: '10+ Code Templates',
      description: 'Ready-to-use indicators'
    },
    {
      title: 'Advanced Alert System Version 2',
      description: 'Next-generation notification system'
    },
    {
      title: 'Code Comments & Documentation',
      description: 'AI-explained code with detailed comments'
    }
  ];

  const premiumFeatures = [
    {
      title: 'Unlimited Generations',
      description: 'Generate as many indicators as you need - no limits!'
    },
    {
      title: 'Advanced AI Powered',
      description: 'Access to advanced AI for better code quality'
    },
    {
      title: 'Built-in SL & TP',
      description: 'Stop Loss & Take Profit automatically integrated'
    },
    {
      title: 'Generation History',
      description: 'Access all your previously generated code'
    },
    {
      title: '50+ Code Templates',
      description: 'Premium collection of ready-to-use indicators'
    },
    {
      title: 'Advanced Alert System Version 3',
      description: 'Latest alert technology with advanced features'
    },
    {
      title: 'Template Alert to Telegram',
      description: 'Send trading alerts directly to your Telegram'
    },
    {
      title: 'Code Comments & Documentation',
      description: 'AI-explained code with detailed comments'
    }
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all">
                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                  <Dialog.Title className="text-3xl font-bold text-gray-900">
                    Upgrade to Premium
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={28} />
                  </button>
                </div>

                {/* Plans Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* VIP Plan */}
                  <div className="border-2 border-gray-200 rounded-2xl p-6 flex flex-col hover:border-cyan-300 transition-all">
                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Star className="text-yellow-500" size={24} />
                        <h3 className="text-2xl font-bold text-gray-900">VIP Plan</h3>
                      </div>
                      <p className="text-4xl font-bold text-gray-900 mb-2">
                        $5.00<span className="text-lg font-normal text-gray-600">/month</span>
                      </p>
                      <p className="text-sm text-gray-600">Perfect for active traders</p>
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-6 flex-grow">
                      {vipFeatures.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="bg-green-100 rounded-full p-1 mt-0.5 flex-shrink-0">
                            <Check size={16} className="text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{feature.title}</p>
                            <p className="text-xs text-gray-600">{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Button */}
                    <button
                      onClick={() => handleUpgrade('vip')}
                      className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg"
                    >
                      Choose VIP - $5
                    </button>
                  </div>

                  {/* Premium Plan */}
                  <div className="relative border-2 border-cyan-400 rounded-2xl p-6 flex flex-col bg-gradient-to-br from-cyan-50 to-white shadow-lg">
                    {/* Most Popular Badge */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">
                        MOST POPULAR
                      </span>
                    </div>

                    {/* Plan Header */}
                    <div className="text-center mb-6 mt-2">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Crown className="text-yellow-500" size={24} />
                        <h3 className="text-2xl font-bold text-gray-900">Premium Plan</h3>
                      </div>
                      <p className="text-4xl font-bold text-gray-900 mb-2">
                        $15.00<span className="text-lg font-normal text-gray-600">/month</span>
                      </p>
                      <p className="text-sm text-gray-600">Ultimate trading toolkit</p>
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-6 flex-grow">
                      {premiumFeatures.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="bg-green-100 rounded-full p-1 mt-0.5 flex-shrink-0">
                            <Check size={16} className="text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{feature.title}</p>
                            <p className="text-xs text-gray-600">{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Button */}
                    <button
                      onClick={() => handleUpgrade('premium')}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-xl"
                    >
                      Choose Premium - $15
                    </button>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-yellow-800">
                    <strong>Daily Limit Reached!</strong> You've used all your free generations for today. Upgrade to VIP for 1000 monthly generations or Premium for unlimited access.
                  </p>
                </div>

                {/* Maybe Later Button */}
                <div className="flex justify-center">
                  <button
                    onClick={onClose}
                    className="px-8 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                  >
                    Maybe Later
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
